// src/components/OfferPdfPreviewModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { updateOfferPdfOverrides } from "../../utils/api-offers";
import {
  generateOfferPDF,
  buildCommercialTextFrom,
  defaultGeneralConditions,
  defaultPurchaseNotes,
} from "../../utils/offer-pdf";
import { labConfig as getLabConfig } from "../../utils/lab-config";
import { downloadBlob } from "../../utils/file-utils";

/* === LOGO: URL provista por vos (Supabase Storage) ======================= */
const LAB_LOGO_URL =
  "https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/Logo%20Calins.png";
/* ======================================================================== */

/**
 * Helper para corregir el typo en la dirección del laboratorio
 */
function sanitizeLabAddress(address) {
  const s = String(address ?? "").trim();
  if (!s) return s;

  return s
    .replaceAll("Rodríguez Pella", "Rodríguez Peña")
    .replaceAll("Rodriguez Pella", "Rodriguez Peña")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Helper para procesar direcciones de clientes antiguos que vienen en un solo string
 */
function parseAddressPartsFromSingleField(address) {
  const raw = String(address ?? "").trim();
  let street = "";
  let locality = "";
  let province = "";

  if (raw) {
    const norm = raw.replace(/\s*–\s*/g, "-").replace(/\s*—\s*/g, "-");
    const parts = norm
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    street = parts[0] || "";
    const rest = parts.slice(1).join(", ").trim();

    if (rest) {
      const byDash = rest.split("-").map((s) => s.trim()).filter(Boolean);
      const byComma = rest.split(",").map((s) => s.trim()).filter(Boolean);

      if (byDash.length >= 2) {
        locality = byDash[0] || "";
        province = byDash[1] || "";
      } else if (byComma.length >= 2) {
        locality = byComma[0] || "";
        province = byComma[1] || "";
      } else {
        locality = rest;
      }
    }
  }

  return {
    street: street.replace(/\s+/g, " ").trim(),
    locality: locality.replace(/\s+/g, " ").trim(),
    province: province.replace(/\s+/g, " ").trim(),
  };
}

function formatMoney(value, currency = "ARS") {
  const amount = Number(value) || 0;
  const symbol = String(currency || "").toUpperCase().includes("USD") ? "US$" : "$";
  return `${symbol} ${amount.toFixed(2)}`;
}

function safePdfFileName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
    .replace(/[. ]+$/g, "")
    .slice(0, 140);
}

function buildOfferPdfFileName(offer = {}) {
  const code = safePdfFileName(offer?.code || "Oferta");
  const clientName = safePdfFileName(offer?.client?.name || offer?.client_name || "Cliente");
  return `${code} - ${clientName}.pdf`;
}

function calcDiscountSummary(offer = {}) {
  const services = Array.isArray(offer?.services) ? offer.services : [];
  const subtotalFromServices = services.reduce((acc, row) => {
    const qty = Number(row?.qty ?? row?.quantity ?? 0) || 0;
    const price = Number(row?.price ?? row?.unit_price ?? 0) || 0;
    return acc + qty * price;
  }, 0);
  const subtotalBase = services.length
    ? subtotalFromServices
    : Number(offer?.subtotal) || 0;

  const ivaRate =
    Number(offer?.iva_rate) > 1
      ? Number(offer?.iva_rate) / 100
      : Number(offer?.iva_rate) || 0.21;

  const discountType = String(offer?.discount_type || "percentage");
  const discountValue = Math.max(0, Number(offer?.discount) || 0);
  const discountAmount =
    discountType === "fixed"
      ? Math.min(subtotalBase, discountValue)
      : Math.min(subtotalBase, subtotalBase * (discountValue / 100));
  const subtotalAfterDiscount = Math.max(0, subtotalBase - discountAmount);
  const ivaAmount = subtotalAfterDiscount * ivaRate;
  const totalWithDiscount = subtotalAfterDiscount + ivaAmount;

  return {
    subtotalBase,
    discountType,
    discountValue,
    discountAmount,
    subtotalAfterDiscount,
    ivaAmount,
    totalWithDiscount,
  };
}

export default function OfferPdfPreviewModal({ isOpen, onClose, offer, onSaved }) {
  const [overrides, setOverrides] = useState(offer?.pdf_overrides || {});
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const discountSummary = useMemo(() => calcDiscountSummary(offer), [offer]);

  useEffect(() => {
    if (!isOpen) return;
    setOverrides(offer?.pdf_overrides || {});
    setResetConfirmOpen(false);
  }, [isOpen, offer?.id]);

  // Config del laboratorio con limpieza de dirección y logo inyectado
  const cfg = useMemo(() => {
    const base = getLabConfig() || {};
    return { 
      ...base, 
      logoUrl: LAB_LOGO_URL,
      address: sanitizeLabAddress(base.address) 
    };
  }, []);

  const defaults = useMemo(() => {
    if (!offer) {
      return { commercial: "", special: "", general: "", notes: "", title: "COTIZACIÓN" };
    }
    return {
      commercial: buildCommercialTextFrom(offer),
      special: offer?.special_conditions || "",
      general: defaultGeneralConditions(),
      notes: defaultPurchaseNotes(),
      title: "COTIZACIÓN",
    };
  }, [offer]);

  const effective = {
    title: (overrides?.title ?? defaults.title) || "COTIZACIÓN",
    commercial: (overrides?.commercial ?? defaults.commercial) || "",
    special: (overrides?.special ?? defaults.special) || "",
    general: (overrides?.general ?? defaults.general) || "",
    notes: (overrides?.notes ?? defaults.notes) || "",
  };

  if (!isOpen || !offer) return null;

  async function handleSave() {
    await updateOfferPdfOverrides(offer.id, {
      title: toNullable(overrides.title),
      commercial: toNullable(overrides.commercial),
      special: toNullable(overrides.special),
      general: toNullable(overrides.general),
      notes: toNullable(overrides.notes),
    });
    onSaved?.();
  }

  async function handlePdf() {
    const blob = await generateOfferPDF({ ...offer, pdf_overrides: overrides }, cfg);
    downloadBlob(blob, buildOfferPdfFileName(offer));
  }

  function handleResetAll() {
    setResetConfirmOpen(true);
  }

  function confirmResetAll() {
    setOverrides({});
    setResetConfirmOpen(false);
  }

  function cancelResetAll() {
    setResetConfirmOpen(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto">
      {resetConfirmOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-background p-5 shadow-2xl">
            <div className="text-lg font-semibold">¿Resetear todo?</div>
            <div className="mt-2 text-sm text-muted-foreground space-y-2">
              <p>
                Esto borrará todos los cambios manuales del PDF y volverá a los
                valores automáticos de la oferta.
              </p>
              <p>No afecta la oferta guardada hasta que presiones “Guardar”.</p>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted/50"
                onClick={cancelResetAll}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-500"
                onClick={confirmResetAll}
              >
                Sí, resetear
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="m-4 w-full max-w-6xl rounded-2xl bg-background text-foreground shadow-xl grid grid-cols-1 lg:grid-cols-2">
        {/* Panel de edición */}
        <div className="p-4 space-y-4 border-r border-border">
          <h3 className="text-lg font-semibold">Previsualizar y editar PDF</h3>

          <SmallInput
            label="Título del documento"
            value={overrides.title ?? defaults.title}
            onChange={(v) => setOverrides((o) => ({ ...o, title: v || null }))}
          />

          <Editor
            label="Condiciones comerciales (texto final)"
            value={overrides.commercial ?? defaults.commercial}
            placeholder=""
            onChange={(v) => setOverrides((o) => ({ ...o, commercial: v || null }))}
            onAuto={() => setOverrides((o) => ({ ...o, commercial: null }))}
          />
          <Editor
            label="Condiciones comerciales especiales"
            value={overrides.special ?? defaults.special}
            placeholder=""
            onChange={(v) => setOverrides((o) => ({ ...o, special: v || null }))}
            onAuto={() => setOverrides((o) => ({ ...o, special: null }))}
          />
          <Editor
            label="Condiciones generales"
            value={overrides.general ?? defaults.general}
            placeholder=""
            onChange={(v) => setOverrides((o) => ({ ...o, general: v || null }))}
            onAuto={() => setOverrides((o) => ({ ...o, general: null }))}
          />
          <Editor
            label="Notas particulares"
            value={overrides.notes ?? defaults.notes}
            placeholder=""
            onChange={(v) => setOverrides((o) => ({ ...o, notes: v || null }))}
            onAuto={() => setOverrides((o) => ({ ...o, notes: null }))}
          />

          <div className="flex gap-2">
            <button
              className="border rounded-lg px-3 py-1.5"
              onClick={handleResetAll}
              title="Revertir todos los cambios manuales del PDF"
            >
              Resetear todo
            </button>
            <div className="grow" />
            <button className="border rounded-lg px-3 py-1.5" onClick={onClose}>
              Cancelar
            </button>
            <button
              className="rounded-lg bg-emerald-600 text-white px-3 py-1.5"
              onClick={handleSave}
            >
              Guardar
            </button>
            <button
              className="rounded-lg bg-blue-600 text-white px-3 py-1.5"
              onClick={handlePdf}
            >
              Generar PDF
            </button>
          </div>
        </div>

        {/* Panel de vista previa HTML */}
        <div className="p-4 space-y-4">
          <HeaderPreview offer={offer} lab={cfg} title={effective.title} />
          <PreviewItems offer={offer} />
          <DiscountPreview offer={offer} summary={discountSummary} />

          <Block title="Condiciones comerciales" text={effective.commercial} />
          {effective.special?.trim() && (
            <Block title="Condiciones comerciales especiales" text={effective.special} />
          )}
          <Block title="Condiciones generales" text={effective.general} />
          <Block title="Notas" text={effective.notes} />

        </div>
      </div>
    </div>
  );
}

/* ==================== Subcomponentes / Helpers ==================== */

function HeaderPreview({ offer, lab, title }) {
  const client = offer?.client || {};

  // ✅ Contacto a mostrar: priorizamos el cargado en la OFERTA (negociación)
  // y si no existe, usamos el contacto de referencia del cliente.
  const contactName =
    String(offer?.contact_name || "").trim() || String(client.contact || "").trim();
  const contactEmail =
    String(offer?.contact_email || "").trim() || String(client.email || "").trim();

  // Lógica de desglosado de dirección de cliente
  const parsed = parseAddressPartsFromSingleField(client.address);
  // Priorizamos campos individuales, si no existen usamos el parseado del campo único
  const street = String(client.street || client.address || parsed.street || "").trim();
  const locality = String(client.locality || parsed.locality || "").trim();
  const province = String(client.province || parsed.province || "").trim();

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="shrink-0">
          {lab?.logoUrl && (
            <img
              src={lab.logoUrl}
              alt="Logo laboratorio"
              className="h-12 w-auto object-contain"
            />
          )}
        </div>

        <div className="text-right">
          <div className="text-xl font-semibold tracking-wide">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Nº: {offer?.code || "—"}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {/* Tarjeta Laboratorio */}
        <div className="rounded-md border border-border p-3">
          <div className="font-medium text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Laboratorio
          </div>
          <div className="font-semibold break-words">{lab?.name || "Laboratorio"}</div>
          {lab?.cuit && <div className="break-words">CUIT: {lab.cuit}</div>}
          {lab?.address && (
            <div className="break-words text-muted-foreground">{lab.address}</div>
          )}
          {(lab?.phone || lab?.email) && (
            <div className="text-xs text-muted-foreground mt-1 break-words">
              {lab?.phone ? `Tel: ${lab.phone}` : ""}
              {lab?.phone && lab?.email ? " · " : ""}
              {lab?.email ? `Email: ${lab.email}` : ""}
            </div>
          )}
        </div>

        {/* Tarjeta Cliente */}
        <div className="rounded-md border border-border p-3">
          <div className="font-medium text-xs text-muted-foreground uppercase tracking-wider mb-1">
            Cliente
          </div>
          <div className="font-semibold break-words mb-1">{client.name || "—"}</div>

          {/* Datos de contacto */}
          <div className="text-muted-foreground mb-2">
            {contactName && <div className="break-words">{contactName}</div>}
            {contactEmail && <div className="break-words italic">{contactEmail}</div>}
          </div>

          {/* Dirección en 3 renglones */}
          <div className="text-sm space-y-0.5 border-t border-border/50 pt-1">
            {street && (
              <div className="break-words">
                <span className="text-muted-foreground">Dir:</span> {street}
              </div>
            )}
            {locality && (
              <div className="break-words">
                <span className="text-muted-foreground">Loc:</span> {locality}
              </div>
            )}
            {province && (
              <div className="break-words">
                <span className="text-muted-foreground">Prov:</span> {province}
              </div>
            )}
          </div>

          {(client.cuit || client.tax_id || client.client_number) && (
            <div className="text-xs mt-2 pt-1 border-t border-border/50 text-muted-foreground">
              {client.cuit || client.tax_id ? `CUIT: ${client.cuit || client.tax_id}` : ""}
              {client.client_number ? ` | Nº Cli: ${client.client_number}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==================== Resto de componentes (sin cambios) ==================== */

function Editor({ label, value, placeholder, onChange, onAuto }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium">{label}</label>
        <button type="button" className="text-xs underline text-primary" onClick={onAuto}>
          Usar automático
        </button>
      </div>
      <textarea
        className="w-full rounded-lg border border-border bg-background px-3 py-2 min-h-[110px] text-sm"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SmallInput({ label, value, onChange }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Utils */
function toNullable(v) {
  const s = v == null ? "" : String(v);
  return s.trim() ? v : null;
}

/* ======= Componentes que ya estaban en tu archivo (sin tocar) ======= */
/* Nota: los dejo tal cual estaban para no romper nada. */

function Block({ title, text }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-2">
        {title}
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed">{text}</div>
    </div>
  );
}

function PreviewItems({ offer }) {
  const items = offer?.services || offer?.items || [];
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs font-semibold tracking-wide uppercase text-muted-foreground mb-2">
        Ítems
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={it?.id || idx} className="rounded-md border border-border/50 p-2">
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium break-words">
                {it?.description || it?.name || it?.service_name || "—"}
              </div>
              <div className="text-xs text-muted-foreground whitespace-nowrap">
                {Number(it?.qty ?? it?.quantity ?? 1)} x {Number(it?.price ?? it?.unit_price ?? 0)}
              </div>
            </div>
            {(it?.ref || it?.reference) && (
              <div className="text-xs text-muted-foreground mt-1">
                Ref: {it?.ref || it?.reference}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscountPreview({ offer, summary }) {
  if (!offer) return null;

  const hasDiscount = Number(summary?.discountAmount) > 0;
  const currency = offer?.price_currency || "ARS";
  const discountType = String(summary?.discountType || "percentage");
  const discountLabel =
    discountType === "fixed"
      ? formatMoney(summary?.discountValue, currency)
      : `${Number(summary?.discountValue || 0)}%`;

  return (
    <div className="rounded-lg border border-amber-400/40 bg-amber-500/5 p-3">
      <div className="mb-2 text-xs font-semibold tracking-wide uppercase text-amber-700 dark:text-amber-400">
        Descuento aplicado
      </div>

      {hasDiscount ? (
        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div className="text-muted-foreground">Tipo</div>
          <div className="font-medium">{discountType === "fixed" ? "Monto fijo" : "Porcentual"}</div>

          <div className="text-muted-foreground">Valor cargado</div>
          <div className="font-medium">{discountLabel}</div>

          <div className="text-muted-foreground">Subtotal sin descuento</div>
          <div className="font-medium">{formatMoney(summary?.subtotalBase, currency)}</div>

          <div className="text-muted-foreground">Descuento aplicado</div>
          <div className="font-medium text-amber-700 dark:text-amber-400">
            - {formatMoney(summary?.discountAmount, currency)}
          </div>

          <div className="text-muted-foreground">Subtotal con descuento</div>
          <div className="font-medium">{formatMoney(summary?.subtotalAfterDiscount, currency)}</div>

          <div className="text-muted-foreground">IVA recalculado</div>
          <div className="font-medium">{formatMoney(summary?.ivaAmount, currency)}</div>

          <div className="font-semibold text-foreground">Total final</div>
          <div className="font-semibold text-primary">{formatMoney(summary?.totalWithDiscount, currency)}</div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          No hay descuento aplicado. El total se genera sobre el subtotal de la oferta.
        </div>
      )}
    </div>
  );
}
